<template>
  <div class="checkout-view">
    <b-container>
      <section class="section section--loader" v-if="searching">
        <loader size="large" :show_timing_slot="true" />
      </section>
      <section class="section" v-else>
        <b-row>
          <b-col md="12" xl="9">
            <checkout-content-block
              :hotel="hotel_details"
              :solution="selected_solution"
            />
          </b-col>
          <b-col md="12" xl="3">
            <sidebar
              :external_checkout_conditions="[
                {
                  sidebar_validation: (selected_solution.accommodation ? selected_solution.accommodation.multiroom_assignment : false),
                  sidebar_message: 'Uno o più gruppi non sono adatti alle camere selezionate. Scegli altre camere!',
                  sidebar_lock: true,
                }
              ]"
              :validate_on_mount="validate_on_mount"
              :integration="integration"
              :availability="get_availability()"
              :live_price="quoted_live_price"
              :loading="loading"
              :purchasing="purchasing"
              :completed="completed"
              :discount_codes="discount_codes"
              @purchase="sidebar_data => { book(sidebar_data) }"
              @commit="event => { this.$store.commit(event.name, event.payload) }">

              <!--slot riepilogo ordine-->
              <template #resume>
                <resume
                  :pricing="resume.pricing"
                  class="checkout-view__resume"
                >
                  <template #message>
                    <span class="text--sm d-block mt-3">Le spese potrebbero essere state ricalcolate.</span>
                  </template>
                </resume>
              </template>

              <!--slot messaggi di stato-->
              <template #message>
                <!--acquisto completato-->
                <div v-if="completed" class="vb-sidebar__block">
                  <div v-if="success" v-html="'Acquisto completato con successo'" />
                  <div v-else v-html="'C\'è stato un problema.'" />
                </div>
                <!--acquisto in corso-->
                <div v-if="!completed && purchasing" class="text-center">
                  <loader />
                </div>
              </template>

              <!--slot libero prima del pulsante di acquisto-->
              <template #before_purchase>
                <p class="checkout-view__disclaimer">
                  Se prenoti dichiari di accettare la <a href="https://www.booking.com/content/privacy.it.html" target="_blank">Privacy policy</a>
                  e le <a href="https://www.booking.com/content/terms.it.html" target="_blank">Condizioni d'uso</a> di <span class="text-primary">Booking.com</span>
                </p>
              </template>
            </sidebar>
          </b-col>
        </b-row>
      </section>

      <loader-modal :show="!completed && purchasing" service="book" />
    </b-container>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { service_handler } from '@src/services'
import { custom_event, get_config } from "@src/utilities"
import { markup, normalize_allocation } from '@bookingv3/utilities'
import CheckoutContentBlock from '@bookingv3/components/views/content-blocks/checkout'
import Resume from '@bookingv3/components/blocks/resume/resume'

export default {
  name: 'checkout-view',

  components: {
    'checkout-content-block': CheckoutContentBlock,
    'resume': Resume,
  },

  props: {
  },

  data() {
    return {
      loading: false,                         // caricamento dati
      purchasing: false,                      // sidebar completata -> acquisto in corso
      completed: false,                       // acquisto completato
      success: false,                         // esito della purchase() -> cambia il messaggio mostrato all'utente
      validate_on_mount: true,
      message: '',
      quote: {},
    }
  },

  provide() {
    return {
      comments_checker: this.set_comments
    }
  },

  computed: {
    ...mapState({
      // stato globale
      searching: state => state.searching,
      discount_codes: state => state.discount_codes,

      // stato del modulo livebkv3
      hotel_details: state => state.livebkv3.hotel_details,
      selected_solution: state => state.livebkv3.selected_solution,
    }),

    /**
     * Configurazione integrazione per la sidebar.
     * Estende la config base ($config.integration) con dati hotel-level dallo store:
     * - bookerAddressRequired: flag dal backend che indica se l'hotel richiede indirizzo del prenotante
     */
    integration() {
      return {
        ...this.$config.integration,
        bookerAddressRequired: this.hotel_details && this.hotel_details.booker_address_required || false,
      }
    },

    // Markup sul prezzo della quote
    quoted_live_price(){
      return markup(this.quote.data.accommodation.price.chargeable_online.booker_currency).live_price
    },

    // Struttura dei prodotti da mandare alla quote/preview.
    // L'allocation viene normalizzata: se il prodotto non definisce fasce bambini,
    // i bambini sono conteggiati come adulti.
    products_preview() {
      return this.selected_solution.accommodation.products.map(({ id, allocation }) => ({
        id,
        allocation: normalize_allocation(allocation)
      }))
    },

    // Genera l'estratto in alto alla sidebar considerando i chargeable true e false
    resume() {
      const {accommodation} = this.quote.data
      let {extra_charges} = accommodation.price

      // funzione di mapping per i total_amount
      const map_total_amount = (arr) => arr.map(item => ({
        ...item,
        total_amount: item.total_amount.booker_currency,
      }))

      // mapping extra_charges
      extra_charges = Object.fromEntries(
        Object.entries(extra_charges).map(([key, value]) => {
          const mapped_value = map_total_amount(value)
          return [key, mapped_value]
        })
      )

      // struttura dati per il blocco di recap
      return {
        pricing: {
          base: accommodation.price.base.booker_currency,
          book: accommodation.price.chargeable_online.booker_currency,
          live_price: this.quoted_live_price,
          extra_charges: {
            ...extra_charges,
            non_conditional: {
              chargeable: extra_charges.non_conditional.filter(item => item.chargeable_online),
              not_chargeable: extra_charges.non_conditional.filter(item => !item.chargeable_online)
            },
          }
        },
      }
    }
  },

  created() {
    this.preview()
  },

  mounted() {
  },

  methods: {

    // Chiamata alla preview(quote)
    preview() {
      let params = {
        accommodation: this.selected_solution.accommodation.hotel_id,
        checkin: this.selected_solution.accommodation.checkin,
        checkout: this.selected_solution.accommodation.checkout,
        products: this.products_preview
      }

      this.$store.commit('searching', true)

      service_handler(
        'quote',
        params
      ).then(
        (success) => {
          this.quote = success.data.quoted
          this.$store.commit('searching', false)
          this.$store.commit('livebkv3/resume', this.resume)

          let items = []
          let accommodation = success.data.quoted.data.accommodation
          let hotelName = this.hotel_details.name.it || Object.values(this.hotel_details.name)[0]

          accommodation.products.forEach(block => {

            items.push({
              "item_id": accommodation.id + "-" + block.id,
              "item_name": hotelName + " - "+ block.room,
              "item_brand": "",
              "price": markup(block.price.chargeable_online.booker_currency).live_price,
              "item_category": "",
            })

          })

          this.ga4QuotedItems = items

          custom_event({
            event: 'begin_checkout',
            ecommerce: {
              currency: "EUR",
              items: items,
              transaction_id: "",
              affiliation: "",
              value: items.reduce((sum, item) => sum + item.price, 0), // Sum of (price * quantity) for all items.
              tax: 0,
              shipping: 0,
              coupon: "",
            }
          })
          custom_event({
            event: 'add_to_cart',
            ecommerce: {
              currency: "EUR",
              items: items,
              transaction_id: "",
              affiliation: "",
              value: items.reduce((sum, item) => sum + item.price, 0), // Sum of (price * quantity) for all items.
              tax: 0,
              shipping: 0,
              coupon: "",
            }
          })

        },
      )
    },

    // Struttura dati che serve alla book
    get_guests_products() {
      return this.selected_solution.accommodation.products.map(product => {
        return {
          id: product.id,
          guests: [] //facoltativo
        }
      })
    },

    // Info statiche per mailing che be non conosce
    hotel_details_for_bookingData() {
      return {
        id: this.hotel_details.id,
        checkin_checkout_times: this.hotel_details.checkin_checkout_times || [],
        important_information: this.hotel_details.description.important_information || '',
        description: this.hotel_details.description.text || '',
        policies: this.hotel_details.policies || [],
        rooms: this.hotel_details.rooms || [],
      }
    },

    // Chiamata alla book
    book(sidebar_data) {
      let book = {
        bookingData: {
          payments: sidebar_data.payments,
          booker_email: sidebar_data.guest.payload.guestEmail,
          booker_telephone: sidebar_data.guest.payload.guestPhone,
          comments: this.message,
          live_price: this.quoted_live_price,
          order_token: this.quote.data.order_token,
          products: this.get_guests_products(),
          //booker_country: "it",
          //travel_purpose: "leisure",
          //user_platform: "desktop",
          //live_price: 666,

          hotel_details: this.hotel_details_for_bookingData(),
          selected_rooms: this.selected_solution.rooms || [], // camere effettivamente prenotate, per be
        },
        guestFirstName: sidebar_data.guest.payload.guestFirstName,
        guestLastName: sidebar_data.guest.payload.guestLastName,
        guestEmail: sidebar_data.guest.payload.guestEmail,
        guestEmailConf: sidebar_data.guest.payload.guestEmailConf,
        guestPhone: sidebar_data.guest.payload.guestPhone,
        guestPrivacy: sidebar_data.guest.payload.guestPrivacy,
        guestGdpr: sidebar_data.guest.payload.guestGdpr
      }

      if (sidebar_data.guest.payload.beneficiaryId) book.beneficiaryId = sidebar_data.guest.payload.beneficiaryId

      if (sidebar_data.booker.payload !== false) {
        book.guestAddress = sidebar_data.booker.payload.booker_address
        book.guestCity = sidebar_data.booker.payload.booker_city
      }

      if (sidebar_data.bill_to_pax.payload !== false) {
        book.guestAddress = sidebar_data.bill_to_pax.payload.guestAddress
        book.guestCity = sidebar_data.bill_to_pax.payload.guestCity
        book.guestPostalCode = sidebar_data.bill_to_pax.payload.guestPostalCode
        book.guestProvince = sidebar_data.bill_to_pax.payload.guestProvince
        book.guestFiscalCode = sidebar_data.bill_to_pax.payload.guestFiscalCode
      }
      
      this.purchasing = true
      this.success = false
      window.scrollTo(0,0)

      service_handler(
        'book',
        book,
        { track: 'checkout' },
      ).then(
        (success) => {
          this.success = true
          this.purchasing = false
          this.completed = true
          window.scrollTo(0,0) // la sidebar sparisce quasi per intero => riporto lo scroll della pagina in alto

          custom_event('confirmedCheckout', get_config('integration.company'), success.data.live_price, 'partnerBehaviour')
          if(success.data.boughtProductTypes){
            success.data.boughtProductTypes.forEach(productType => {
              custom_event('book', productType, success.data.live_price, 'productType')
            })
          }

          custom_event({
            event: 'purchase',
            ecommerce: {
              currency: "EUR",
              items: this.ga4QuotedItems,
              transaction_id: success.data.confirmcode,
              affiliation: "",
              value: this.ga4QuotedItems.reduce((sum, item) => sum + item.price, 0), // Sum of (price * quantity) for all items.
              tax: 0,
              shipping: 0,
              coupon: "",
            }
          })
          
          // da valutare: a prenotazione completata cancello i dati necessari al checkout ( => per un'altra prenotazione bisogna ripassare per la ricerca)
          //this.$store.commit('livebkv3/hotel_details', {})
          //this.$store.commit('livebkv3/selected_solution', {})

          const resolved = this.$router.resolve({
            name: 'confirm',
            params: {
              confirm_code: success.data.confirmcode,
              reservation_id: success.data.reservation_id.toString()
            }
          })
          window.location.href = resolved.href
        },
        (error) => {
          this.log('[book] - error')
          this.log(error.response)
          this.validate_on_mount = true
          this.success = false
          this.purchasing = false
          custom_event('failedCheckout', get_config('integration.company'), book.bookingData.live_price, 'partnerBehaviour')
        }
      )
    },

    // Gestione box dei commenti
    set_comments(message) {
      this.message = message
    },
  },
}
</script>

<style lang="scss" scoped>

  .checkout-view {

    &__resume {
      background:$white;
      padding: 20px;
      margin-bottom: 20px;
      border-radius: $border-radius;

      &-message {
        font-size: $font-size-md;
        margin-top: $grid-gutter-width/2;
        margin-bottom: 0;
        padding: 3px 8px;
        text-align: center;
      }
    }

    &__disclaimer {
      margin-bottom: 0;
      text-align: left !important;
      font-size: $font-size-md;
    }
  }

</style>